const MYSTERY_REWARD_CODES = Object.freeze({
  20: 'PM-S26-20-K7X4Q9',
  25: 'PM-S26-25-N3R8WL',
  30: 'PM-S26-30-T9F2KM',
  40: 'PM-S26-40-V6Q8XP',
  50: 'PM-S26-50-J4M7CZ'
});

const MYSTERY_REWARD_AMOUNTS = Object.freeze([20, 25, 30, 40, 50]);

function buildMysteryRewardPool() {
  const rewards = [
    ...Array(63).fill(20),
    ...Array(18).fill(25),
    ...Array(11).fill(30)
  ];

  // A fixed shuffle avoids independent random assignment. Positions are
  // deterministic hash buckets, not chronological submission numbers: no €40 or
  // €50 sits in bucket 1-60, but every visitor draws a bucket independently, so
  // a high reward can still be assigned to the very first submission. The
  // bucket layout controls the long-run mix, not the order rewards appear in.
  let state = 0x50414d55;
  for (let index = rewards.length - 1; index > 0; index -= 1) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    const swapIndex = state % (index + 1);
    [rewards[index], rewards[swapIndex]] = [rewards[swapIndex], rewards[index]];
  }

  const pool = [];
  let rewardIndex = 0;
  const fortyPositions = new Set([63, 69, 75, 81, 91, 96]);
  const fiftyPositions = new Set([86, 99]);
  for (let position = 1; position <= 100; position += 1) {
    if (fiftyPositions.has(position)) {
      pool.push(50);
    } else if (fortyPositions.has(position)) {
      pool.push(40);
    } else {
      pool.push(rewards[rewardIndex]);
      rewardIndex += 1;
    }
  }
  return Object.freeze(pool);
}

const MYSTERY_REWARD_POOL = buildMysteryRewardPool();

function validMysteryRewardPool(pool) {
  const expectedCounts = { 20: 63, 25: 18, 30: 11, 40: 6, 50: 2 };
  const counts = pool.reduce((result, amount) => {
    result[amount] = (result[amount] || 0) + 1;
    return result;
  }, {});
  const hasEarlyHighReward = pool.slice(0, 60).some((amount) => amount === 40 || amount === 50);

  return pool.length === 100
    && !hasEarlyHighReward
    && Object.entries(expectedCounts).every(([amount, count]) => counts[amount] === count);
}

function validMysteryRewardCodes(codes) {
  if (Object.keys(codes).length !== MYSTERY_REWARD_AMOUNTS.length) return false;

  const seen = new Set();
  return MYSTERY_REWARD_AMOUNTS.every((amount) => {
    const code = codes[amount];
    if (typeof code !== 'string' || seen.has(code)) return false;
    seen.add(code);

    // The tier is embedded in the code, so a mis-mapped amount fails here: a
    // €20 reward can never be paired with the €25, €30, €40 or €50 code.
    const match = /^PM-S26-(\d{2})-[A-Z0-9]{6}$/.exec(code);
    return Boolean(match) && Number(match[1]) === amount;
  });
}

// Verified once at load. When this is false the element still registers, but
// every instance stops the reward flow instead of leaving the native Shopify
// form free to subscribe a customer who could never be assigned a code.
const MYSTERY_REWARD_READY = validMysteryRewardPool(MYSTERY_REWARD_POOL)
  && validMysteryRewardCodes(MYSTERY_REWARD_CODES);

class MysteryReward extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('[data-mystery-form]');
    this.tagsInput = this.querySelector('[data-mystery-tags]');
    this.formSuccess = this.querySelector('[data-mystery-form-success]');
    this.formError = this.querySelector('[data-mystery-form-error]');
    this.intro = this.querySelector('[data-mystery-intro]');
    this.unlocking = this.querySelector('[data-mystery-unlocking]');
    this.reveal = this.querySelector('[data-mystery-reveal]');
    this.status = this.querySelector('[data-mystery-status]');
    this.revealStatus = this.querySelector('[data-mystery-reveal-status]');
    this.submitButton = this.querySelector('[data-mystery-submit]');
    this.amount = this.querySelector('[data-mystery-amount]');
    this.code = this.querySelector('[data-mystery-code]');
    this.copyButton = this.querySelector('[data-mystery-copy]');
    this.copyLabel = this.querySelector('[data-mystery-copy-label]');
    this.applyButton = this.querySelector('[data-mystery-apply]');
    this.designMode = this.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);

    this.revealOnly = this.dataset.revealOnly === 'true';

    if (!this.reveal || !this.copyButton || !this.applyButton) return;
    this.copyButton.addEventListener('click', this.handleCopy.bind(this));

    // The campaign has ended, so no form was rendered. The only job left is to
    // hand back a reward this browser already claimed: nothing is submitted and
    // no new reward can be assigned. The popup stays shut unless there is one.
    if (this.revealOnly) {
      const claim = this.getStoredClaim();
      if (this.validClaim(claim) && claim.confirmed) {
        this.renderClaim(claim, true);
        const popup = this.closest('pop-up');
        if (popup && !this.popupDismissed(popup)) this.openParentPopup();
      }
      return;
    }

    if (!this.form || !this.tagsInput) return;
    this.form.addEventListener('submit', this.handleSubmit.bind(this));

    // Either the reward table failed verification, or this browser cannot
    // produce the deterministic SHA-256 bucket. Without both, the same email
    // would no longer map to the same reward, so the form is stopped.
    if (!MYSTERY_REWARD_READY || !this.canHashDeterministically()) {
      this.disableRewards();
      return;
    }

    if (!this.designMode) this.restoreClaim();
  }

  disconnectedCallback() {
    if (this.revealTimer) window.clearTimeout(this.revealTimer);
  }

  // Publishes into Shopify's Web Pixel event stream, which is where GA4 is
  // subscribed. Names carry the required `pamuuc:` prefix — Shopify reserves
  // unprefixed names for its own standard events. Never throws: analytics must
  // not be able to block a reward.
  publishAnalytics(name, payload) {
    try {
      window.Shopify?.analytics?.publish(name, payload);
    } catch (error) {
      // Intentionally silent.
    }
  }

  canHashDeterministically() {
    return Boolean(window.crypto?.subtle && window.TextEncoder);
  }

  disableRewards() {
    this.unavailable = true;
    this.submitButton.disabled = true;
    this.submitButton.classList.add('is-locked');
    this.setStatus(this.dataset.unavailable);
  }

  cookieName() {
    return `pamuuc_mystery_${(this.dataset.campaign || 'campaign').replace(/[^a-z0-9_-]/gi, '_')}`;
  }

  getStoredClaim() {
    let value = null;
    try {
      value = localStorage.getItem(this.dataset.claimKey);
    } catch (error) {
      // The first-party cookie fallback supports restricted storage modes.
    }

    if (!value) {
      const prefix = `${this.cookieName()}=`;
      const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
      if (cookie) value = decodeURIComponent(cookie.slice(prefix.length));
    }

    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch (error) {
      this.clearStoredClaim();
      return null;
    }
  }

  storeClaim(claim) {
    const value = JSON.stringify(claim);
    try {
      localStorage.setItem(this.dataset.claimKey, value);
    } catch (error) {
      // The first-party cookie below remains available when storage is blocked.
    }
    document.cookie = `${this.cookieName()}=${encodeURIComponent(value)}; Max-Age=7776000; Path=/; SameSite=Lax; Secure`;
  }

  clearStoredClaim() {
    try {
      localStorage.removeItem(this.dataset.claimKey);
    } catch (error) {
      // No action is required when storage is unavailable.
    }
    document.cookie = `${this.cookieName()}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
  }

  setBusy(isBusy) {
    this.form.setAttribute('aria-busy', String(isBusy));
    this.submitButton.disabled = isBusy;
    this.submitButton.classList.toggle('is-loading', isBusy);
  }

  setStatus(message, target = this.status) {
    if (!target) return;
    target.textContent = message || '';
    target.hidden = !message;
  }

  normalizeEmail(value) {
    return value.trim().toLowerCase();
  }

  async digestForEmail(email) {
    const source = `${this.dataset.campaign || 'summer-2026'}\u0000${email}`;
    return new DataView(await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(source)));
  }

  positionFromDigest(view) {
    const unbiasedLimit = 4294967200;
    for (let offset = 0; offset <= view.byteLength - 4; offset += 4) {
      const value = view.getUint32(offset);
      if (value < unbiasedLimit) return value % 100;
    }
    return view.getUint32(0) % 100;
  }

  // A short, non-reversing fingerprint of the submitted address. The email
  // itself is never written to storage, but this is stable enough to tell
  // whether a stored claim belongs to the address being submitted now.
  emailIdFromDigest(view) {
    let id = '';
    for (let offset = view.byteLength - 8; offset < view.byteLength; offset += 1) {
      id += view.getUint8(offset).toString(16).padStart(2, '0');
    }
    return id;
  }

  async claimForEmail(email) {
    const view = await this.digestForEmail(email);
    const position = this.positionFromDigest(view);
    const amount = MYSTERY_REWARD_POOL[position];
    return {
      amount,
      code: MYSTERY_REWARD_CODES[amount],
      position: position + 1,
      emailId: this.emailIdFromDigest(view),
      confirmed: false,
      createdAt: Date.now()
    };
  }

  tagsForAmount(amount) {
    const campaign = (this.dataset.campaign || 'summer-2026').replace(/[^a-z0-9-]/gi, '-').toLowerCase();
    return `prospect,newsletter,mystery-${campaign},mystery-${campaign}-${amount}`;
  }

  formPostedSuccessfully() {
    // Scoped to this form only. The ?customer_posted=true parameter is set by
    // every customer form in the theme, including the footer newsletter, so
    // reading it here would confirm a pending claim whose tier tag was never
    // submitted and hand out a code the tier segment does not cover.
    return this.formSuccess?.dataset.mysteryFormSuccess === 'true';
  }

  formHasErrors() {
    return this.formError?.dataset.mysteryFormError === 'true';
  }

  lockPendingSubmission() {
    this.submitButton.disabled = true;
    this.submitButton.classList.add('is-locked');
    this.setStatus(this.dataset.submissionPending);
  }

  async handleSubmit(event) {
    if (this.nativeSubmitReady) {
      this.nativeSubmitReady = false;
      return;
    }

    event.preventDefault();

    if (this.unavailable) {
      this.setStatus(this.dataset.unavailable);
      return;
    }

    this.setStatus('');

    // A repeated click while this page's submission is still in flight, as
    // opposed to a claim already stored from an earlier visit.
    if (this.submissionStarted) {
      this.setStatus(this.dataset.rateLimited);
      return;
    }

    const emailInput = this.form.elements['contact[email]'];
    emailInput.removeAttribute('aria-invalid');
    if (!emailInput.validity.valid) {
      this.setStatus(this.dataset.invalidEmail);
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.focus();
      return;
    }

    this.submissionStarted = true;
    this.setBusy(true);
    try {
      const email = this.normalizeEmail(emailInput.value);
      const claim = await this.claimForEmail(email);

      if (this.designMode) {
        claim.confirmed = true;
        this.submissionStarted = false;
        this.renderClaim(claim, false);
        this.setBusy(false);
        return;
      }

      // The reward belongs to the address, not to the browser. A stored claim
      // is replayed only when this submission is the same address, so a second
      // person on a shared device gets their own reward instead of this one.
      const stored = this.getStoredClaim();
      if (this.validClaim(stored) && stored.emailId === claim.emailId) {
        this.submissionStarted = false;
        this.setBusy(false);
        if (stored.confirmed) this.renderClaim(stored, true);
        else this.lockPendingSubmission();
        return;
      }

      this.tagsInput.value = this.tagsForAmount(claim.amount);
      this.storeClaim(claim);

      // Best effort: the native POST navigates immediately after this, so the
      // beacon may not always flush. mystery_reward_revealed on the return page
      // is the reliable conversion signal.
      this.publishAnalytics('pamuuc:mystery_reward_submitted', {
        campaign: this.dataset.campaign || '',
        amount: claim.amount
      });

      // Re-submit through the native Shopify customer form so Shopify's own
      // marketing-consent and CAPTCHA behavior remains in control.
      this.nativeSubmitReady = true;
      this.setBusy(false);
      this.form.requestSubmit();
    } catch (error) {
      this.submissionStarted = false;
      this.setStatus(this.dataset.error);
      this.setBusy(false);
    }
  }

  restoreClaim() {
    const claim = this.getStoredClaim();
    if (!this.validClaim(claim)) return;

    if (!claim.confirmed && this.formHasErrors()) {
      this.clearStoredClaim();
      return;
    }

    if (!claim.confirmed && this.formPostedSuccessfully()) {
      claim.confirmed = true;
      this.storeClaim(claim);

      // Shopify has accepted the submission and this page is a fresh load, so
      // there is no navigation racing the beacon. This is the conversion.
      this.publishAnalytics('pamuuc:mystery_reward_revealed', {
        campaign: this.dataset.campaign || '',
        amount: claim.amount
      });

      this.renderClaim(claim, false);
      this.openParentPopup();
      return;
    }

    if (claim.confirmed) this.renderClaim(claim, true);
    else this.lockPendingSubmission();
  }

  // Reuses Symmetry's own dismissal cookie, so closing the post-campaign popup
  // stops it reappearing on every page instead of nagging for 90 days.
  popupDismissed(popup) {
    const name = `${encodeURIComponent(`${popup.id}-dismissed`)}=`;
    return document.cookie.split('; ').some((entry) => entry.startsWith(name));
  }

  openParentPopup() {
    const popup = this.closest('pop-up');
    if (!popup) return;

    const openPopup = () => {
      if (typeof popup.open === 'function' && !popup.hasAttribute('open')) popup.open();
    };
    if (customElements.get('pop-up')) {
      openPopup();
    } else {
      customElements.whenDefined('pop-up').then(openPopup);
    }
  }

  validClaim(claim) {
    const amount = Number(claim?.amount);
    return MYSTERY_REWARD_AMOUNTS.includes(amount)
      && claim.code === MYSTERY_REWARD_CODES[amount]
      && Number.isInteger(claim.position)
      && claim.position >= 1
      && claim.position <= 100
      && typeof claim.emailId === 'string'
      && /^[0-9a-f]{16}$/.test(claim.emailId)
      // Rejects a stored claim whose amount no longer matches the bucket it
      // was drawn from, whether edited by hand or left over from an older table.
      && MYSTERY_REWARD_POOL[claim.position - 1] === amount;
  }

  renderClaim(claim, existing) {
    if (!this.validClaim(claim)) {
      this.setStatus(this.dataset.error);
      return;
    }

    const amountLabel = String(Number(claim.amount));
    this.amount.textContent = this.dataset.rewardHeading.replace('__AMOUNT__', amountLabel);
    this.code.textContent = claim.code;
    this.copyButton.dataset.code = claim.code;

    const root = this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
    const discountUrl = new URL(`${root}discount/${encodeURIComponent(claim.code)}`, window.location.origin);
    discountUrl.searchParams.set('redirect', this.dataset.collectionUrl);
    this.applyButton.href = `${discountUrl.pathname}${discountUrl.search}`;

    const modalWindow = this.closest('.modal__window');
    const modalContent = this.closest('.modal__content');
    const resetModalScroll = () => {
      if (modalWindow) modalWindow.scrollTop = 0;
      if (modalContent) modalContent.scrollTop = 0;
    };

    if (document.activeElement instanceof HTMLElement && this.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    if (this.intro) this.intro.hidden = true;
    resetModalScroll();
    if (this.revealTimer) window.clearTimeout(this.revealTimer);

    const showReveal = () => {
      this.classList.remove('is-unlocking');
      if (this.unlocking) this.unlocking.hidden = true;
      this.reveal.hidden = false;
      resetModalScroll();
      window.requestAnimationFrame(resetModalScroll);
      modalWindow?.setAttribute('aria-labelledby', this.amount.id);
      this.revealStatus?.classList.remove('visually-hidden');
      this.setStatus(existing ? this.dataset.alreadyClaimed : '', this.revealStatus);

      this.dispatchEvent(new CustomEvent('pamuuc:mystery-reward:revealed', {
        bubbles: true,
        detail: { amount: Number(claim.amount), existing }
      }));
    };

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!existing && this.unlocking && !reduceMotion) {
      this.reveal.hidden = true;
      this.unlocking.hidden = false;
      this.classList.add('is-unlocking');
      modalWindow?.setAttribute('aria-labelledby', this.unlocking.id);
      this.revealTimer = window.setTimeout(showReveal, 780);
    } else {
      showReveal();
    }
  }

  async handleCopy() {
    const code = this.copyButton.dataset.code;
    if (!code) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const range = document.createRange();
        range.selectNodeContents(this.code);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
      }
      this.copyLabel.textContent = this.dataset.copied;
      this.revealStatus?.classList.add('visually-hidden');
      this.setStatus(this.dataset.copied, this.revealStatus);
    } catch (error) {
      this.revealStatus?.classList.remove('visually-hidden');
      this.setStatus(this.dataset.error, this.revealStatus);
    }
  }
}

// Persistent tab that reopens the popup for visitors who closed it without
// subscribing. Deliberately layered below the cookie banner in CSS.
class MysteryReopen extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.button = this.querySelector('[data-mr-reopen-btn]');
    this.labelEl = this.querySelector('[data-mr-reopen-label]');
    this.popup = document.getElementById(this.dataset.popupId);
    if (!this.button || !this.labelEl || !this.popup) return;

    this.ended = this.dataset.ended === 'true';
    this.designMode = Boolean(window.Shopify?.designMode);

    this.button.addEventListener('click', this.handleClick.bind(this));

    // The modal toggles an `open` attribute, so watching it keeps the tab in
    // step without needing an event the theme does not emit.
    this.observer = new MutationObserver(this.sync.bind(this));
    this.observer.observe(this.popup, { attributes: true, attributeFilter: ['open'] });

    this.sync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  storedClaim() {
    try {
      const raw = localStorage.getItem(this.dataset.claimKey);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  popupDismissed() {
    const name = `${encodeURIComponent(this.dataset.cookieName)}=`;
    return document.cookie.split('; ').some((entry) => entry.startsWith(name));
  }

  shouldShow() {
    if (this.popup.hasAttribute('open')) return false;
    if (this.designMode) return true;

    const claim = this.storedClaim();
    const claimed = Boolean(claim && claim.confirmed);

    // After the campaign the tab exists only to hand back a code already won.
    if (this.ended) return claimed;

    // Someone holding a reward has nothing left to reopen for.
    if (claimed) return false;

    // Otherwise wait until they have actually closed the popup once.
    return this.popupDismissed();
  }

  sync() {
    this.labelEl.textContent = this.ended ? this.dataset.labelClaimed : this.dataset.label;
    this.button.setAttribute('aria-label', this.dataset.aria);
    this.classList.toggle('is-claimed', this.ended);
    this.hidden = !this.shouldShow();
  }

  handleClick() {
    if (typeof this.popup.open === 'function') this.popup.open();
    this.hidden = true;
  }
}

if (!customElements.get('mystery-reward')) {
  customElements.define('mystery-reward', MysteryReward);
}

if (!customElements.get('mystery-reopen')) {
  customElements.define('mystery-reopen', MysteryReopen);
}